import { memo } from "react";
import { useWatch, type UseFormReturn } from "react-hook-form";
import { StepHeading } from "@/features/rentals/components/rental-create/wizard/steps/StepHeading";
import type { RentalCreateFormSchema } from "@/features/rentals/components/rental-create/rental-create-schema";
import type { NewClientData } from "../../schemas";
import {
  ContractSelector,
  type ContractOption,
  type ClientOption,
} from "../wizard/ContractSelector";

interface StepAdminClientContractProps {
  form: UseFormReturn<RentalCreateFormSchema>;
  selectedContractLabel: string;
  onContractSelect: (
    id: number,
    display: string,
    client: { id: number; name: string },
  ) => void;
  onContractClear: () => void;
  searchContracts: (
    term: string,
    signal?: AbortSignal,
  ) => Promise<ContractOption[]>;
  createContract: (
    contractNumber: string,
    clientId: number,
    signal?: AbortSignal,
  ) => Promise<{ id: number; contractNumber: string }>;
  searchClients: (
    term: string,
    signal?: AbortSignal,
  ) => Promise<ClientOption[]>;
  createClient: (
    data: NewClientData,
    signal?: AbortSignal,
  ) => Promise<{ id: number; name: string }>;
}

export const StepAdminClientContract = memo(function StepAdminClientContract({
  form,
  selectedContractLabel,
  onContractSelect,
  onContractClear,
  searchContracts,
  createContract,
  searchClients,
  createClient,
}: StepAdminClientContractProps) {
  const selectedContractId = useWatch({
    control: form.control,
    name: "selectedContractId",
  });
  const errors = form.formState.errors;
  // O cliente vem embutido no contrato e entra na validação da etapa; sem
  // exibir o erro dele, o "Próximo" travaria sem mensagem na tela.
  const error =
    errors.selectedContractId?.message ?? errors.selectedClientId?.message;

  return (
    <div className="space-y-6">
      <StepHeading
        title="Cliente e contrato"
        description="Busque um contrato existente ou crie um novo na hora."
      />

      <ContractSelector
        selectedId={selectedContractId}
        selectedDisplay={selectedContractLabel}
        onSelect={onContractSelect}
        onClear={onContractClear}
        searchContracts={searchContracts}
        createContract={createContract}
        searchClients={searchClients}
        createClient={createClient}
      />

      {error ? <p className="text-sm text-destructive">{error}</p> : null}
    </div>
  );
});
